import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { bucketLabel } from '../utils/bucketLabels';
import './WordEngineSourcing.css';
import './WordBankTriage.css';

// Word Sourcing (Word Engine Chunk W12/W13, 2026-09-26).
// Odin enters a theme (or leaves it blank), picks how many, and runs the agent.
// Candidates land in word_candidates, never in words. A accepts a candidate
// into Bench UNAPPROVED (it still goes through Triage); X rejects it and it is
// never proposed again. Uses only the /api/wordbank/sourcing/* routes.
// The definition shown is Wiktionary's text; Wordnik is only a yes/no tick.

const AXES = ['soundability', 'category_cleanliness', 'fun_fact_surface', 'difficulty', 'brand_fit', 'sensitivity'] as const;
type Axis = typeof AXES[number];
const AXIS_LABEL: Record<Axis, string> = {
  soundability: 'Snd', category_cleanliness: 'Cat', fun_fact_surface: 'Fact',
  difficulty: 'Diff', brand_fit: 'Brand', sensitivity: 'Safe',
};

interface Candidate {
  id: string;
  run_id: string | null;
  word: string;
  part_of_speech: string | null;
  exists_source: 'wordnik' | 'wiktionary' | null;
  fit_score: number | null;
  quality_score: number | null;
  difficulty_score: number | null;
  scores: Partial<Record<Axis, number>> | null;
  reasons: Partial<Record<Axis, string>> | null;
  source_reason: string | null;
  proposed_bucket: string | null;
  real_definition: string | null;
  sense: string | null;
}

interface Dropped {
  word: string;
  reason: string;
  matched?: string;
  in?: string;
  kind?: string;
  sensitivity?: number;
}

interface Run {
  id: string;
  theme: string | null;
  count_asked: number;
  count_kept: number;
  cost_usd: number;
  status: string;
  error: string | null;
  started_at: string | null;
  report: { dropped?: Dropped[] } | null;
}

interface ListResponse {
  rows: Candidate[];
  runs: Run[];
  cap_usd: number;
  default_count: number;
  max_count: number;
  warning?: string;
}

interface Estimate {
  count: number;
  estimate_usd: number;
  cap_usd: number;
  over_cap: boolean;
  max_count: number;
}

type SortKey = 'combined' | 'fit' | 'quality' | 'difficulty';

const REASON_LABEL: Record<string, string> = {
  exact: 'already in the bank',
  variant: 'close variant',
  not_found: 'no dictionary knows it',
  no_wiktionary_definition: 'Wordnik knows it, but no definition we can store',
  uncited_only: 'uncited Wiktionary entry, not confirmed',
  floor: 'Floor: targets identity or a slur',
  malformed: 'not a single word',
  not_scored: 'scorer skipped it',
};

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function money(n: number | null | undefined): string {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

function combined(c: Candidate): number {
  return (c.fit_score ?? 0) + (c.quality_score ?? 0) + (c.difficulty_score ?? 0);
}

export default function WordEngineSourcing() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState('');
  // Chunk W-Content: what the run is sourcing for. Accepted words still land in Bench.
  const [target, setTarget] = useState<'game' | 'social'>('game');
  const [count, setCount] = useState(25);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<{ cost_usd?: number; saved?: unknown[]; dropped?: Dropped[]; reason?: string; refused?: boolean; warning?: string } | null>(null);
  const [sort, setSort] = useState<SortKey>('combined');
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData((await api.get('/api/wordbank/sourcing/candidates?status=new')) as ListResponse);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let live = true;
    api.get(`/api/wordbank/sourcing/estimate?count=${count}`)
      .then((e) => { if (live) setEstimate(e as Estimate); })
      .catch(() => { if (live) setEstimate(null); });
    return () => { live = false; };
  }, [count]);

  const rows = useMemo(() => {
    const r = [...(data?.rows ?? [])];
    const key: Record<SortKey, (c: Candidate) => number> = {
      combined, fit: (c) => c.fit_score ?? 0, quality: (c) => c.quality_score ?? 0, difficulty: (c) => c.difficulty_score ?? 0,
    };
    return r.sort((a, b) => key[sort](b) - key[sort](a) || a.word.localeCompare(b.word));
  }, [data, sort]);

  const current = rows[Math.min(cursor, rows.length - 1)] ?? null;

  useEffect(() => {
    if (current) rowRefs.current.get(current.id)?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const run = async () => {
    setRunning(true);
    setError(null);
    setLastRun(null);
    try {
      const res = await api.post('/api/wordbank/sourcing/run', { theme, count, target });
      setLastRun(res as typeof lastRun);
      await load();
      setCursor(0);
    } catch (err) {
      const e = err as Error & { data?: { reason?: string } };
      setError(e.data?.reason || e.message);
      await load();
    } finally {
      setRunning(false);
    }
  };

  const decide = useCallback(async (c: Candidate, action: 'accept' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/wordbank/sourcing/${c.id}/${action}`, {});
      setData((d) => (d ? { ...d, rows: d.rows.filter((r) => r.id !== c.id) } : d));
    } catch (err) {
      setError(`${c.word}: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (!current || busy) return;
      const k = e.key.toLowerCase();
      if (k === 'a') { e.preventDefault(); decide(current, 'accept'); }
      else if (k === 'x') { e.preventDefault(); decide(current, 'reject'); }
      else if (k === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
      else if (k === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, busy, decide, rows.length]);

  const shownDrops = lastRun?.dropped ?? data?.runs?.[0]?.report?.dropped ?? [];
  const maxCount = data?.max_count ?? 60;

  return (
    <div className="src">
      <div className="src-form">
        <label>
          Target
          <select value={target} onChange={(e) => setTarget(e.target.value as 'game' | 'social')}>
            <option value="game">Game Word</option>
            <option value="social">Social Word</option>
          </select>
        </label>
        <label>
          Theme
          <input type="text" value={theme} maxLength={120} placeholder="Blank for anything" onChange={(e) => setTheme(e.target.value)} />
        </label>
        <label>
          How many
          <input type="number" min={1} max={maxCount} value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(maxCount, Number(e.target.value) || 1)))} />
        </label>
        <div className={`src-est ${estimate?.over_cap ? 'src-over' : ''}`}>
          {estimate
            ? <>Estimated <b>{money(estimate.estimate_usd)}</b> · cap {money(estimate.cap_usd)} per run
                {estimate.over_cap && <> · over the cap, ask for {estimate.max_count} or fewer</>}</>
            : 'Estimating…'}
        </div>
        <button className="wb-refresh" onClick={run} disabled={running || !!estimate?.over_cap}>
          {running ? 'Running…' : 'Run'}
        </button>
      </div>

      {data?.warning && <div className="wb-error">{data.warning}</div>}
      {error && <div className="wb-error">{error}</div>}
      {lastRun && !lastRun.refused && (
        <div className="src-flash">
          Saved <b>{lastRun.saved?.length ?? 0}</b> candidates · this run cost <b>{money(lastRun.cost_usd)}</b>
        </div>
      )}

      {shownDrops.length > 0 && (
        <details className="src-drops" open={!!lastRun}>
          <summary>Filtered out ({shownDrops.length})</summary>
          <ul>
            {shownDrops.map((d, i) => (
              <li key={`${d.word}-${i}`}>
                <b>{d.word}</b>: {REASON_LABEL[d.reason] || d.reason}
                {d.matched && <> of <b>{d.matched}</b>{d.kind ? ` (${d.kind})` : ''}{d.in ? `, in ${d.in}` : ''}</>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="tri-keys">
        <kbd>A</kbd> accept into Bench (unapproved, goes to Triage) <kbd>X</kbd> reject (never proposed again)
        <span className="tri-sep" /> <kbd>J</kbd>/<kbd>K</kbd> or arrows move
        <label className="src-sort">
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="combined">Combined</option>
            <option value="fit">Fit</option>
            <option value="quality">Quality</option>
            <option value="difficulty">Difficulty</option>
          </select>
        </label>
        <button className="wb-refresh" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Reload'}</button>
      </div>

      {!loading && rows.length === 0 && <div className="web-empty">No candidates waiting. Run the agent above.</div>}
      {rows.length > 0 && (
        <table className="src-table">
          <thead>
            <tr>
              <th>Word</th>
              <th>Definition (Wiktionary)</th>
              <th title="Wordnik confirmed the word exists">Wordnik</th>
              <th>Proposed</th>
              <th title="Mean of brand fit and category cleanliness">Fit</th>
              <th title="Mean of soundability and fun-fact surface">Qual</th>
              {AXES.map((a) => <th key={a} className="tri-score">{AXIS_LABEL[a]}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr
                key={c.id}
                ref={(el) => { if (el) rowRefs.current.set(c.id, el); else rowRefs.current.delete(c.id); }}
                className={current && current.id === c.id ? 'tri-row tri-active' : 'tri-row'}
                onClick={() => setCursor(i)}
              >
                <td className="tri-word">
                  {c.word}
                  <div className="src-meta">{c.sense || c.part_of_speech || ''}</div>
                  {c.source_reason && <div className="tri-rownote">{c.source_reason}</div>}
                </td>
                <td className="src-def">{c.real_definition || <span className="wb-dash">not stored</span>}</td>
                <td className="tri-score">{c.exists_source === 'wordnik' ? <span className="tri-stamp-ok">✓</span> : <span className="wb-dash">·</span>}</td>
                <td>{c.proposed_bucket && <span className={`wb-bucket wb-b-${c.proposed_bucket}`}>{bucketLabel(c.proposed_bucket)}</span>}</td>
                <td className="tri-score">{c.fit_score ?? '·'}</td>
                <td className="tri-score">{c.quality_score ?? '·'}</td>
                {AXES.map((a) => {
                  const v = c.scores?.[a];
                  return (
                    <td key={a} className="tri-score" title={c.reasons?.[a] || undefined}>
                      {typeof v === 'number' ? <span className={`wb-score wb-s${v}`}>{v}</span> : <span className="wb-dash">·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && data.runs.length > 0 && (
        <div className="src-runs">
          <h3>Recent runs</h3>
          <table>
            <tbody>
              {data.runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.started_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td>{r.theme || 'no theme'}</td>
                  <td>{r.count_kept}/{r.count_asked} kept</td>
                  <td>{money(r.cost_usd)}</td>
                  <td>{r.status}{r.error ? `: ${r.error}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
